import { client } from '../init/client'
import { log, warn } from '../init/log'
import { getDish, getDishes, getDishList, getList, getPage, getTranslations, getTranslationsRichText } from './content'
import { convert } from './convert'
import { convertJobs } from './jobs'

import type { Dish, List, Page } from './convert'
import type { Locale } from '../init/i18n'
import type { Statistics } from '@juitnow/api-comments'
import type { Document } from '@contentful/rich-text-types'

export function fetchPage(locale: Locale, slug: string): Promise<Page | undefined> {
  return getPage(locale, slug).then((page) => convert(page))
}

/* ========================================================================== */

export function fetchList(locale: Locale, slug: string): Promise<List | undefined> {
  return getList(locale, slug).then((list) => convert(list))
}

/* ========================================================================== */

const translationsCache = new Map<Locale, Record<string, Record<string, any>>>()

export async function fetchTranslations(locale: Locale): Promise<Record<string, Record<string, any>>> {
  let translations = translationsCache.get(locale)
  if (translations) return translations

  log('Downloading translations for "%s"', locale)
  const entries = await getTranslations(locale)

  translations = entries.reduce((q, { fields }) => {
    return Object.assign(q, { [fields.slug]: fields.strings || {} })
  }, {} as Record<string, Record<string, any>>)

  translationsCache.set(locale, translations)
  return translations
}

/* ========================================================================== */

const translationsRichTextCache = new Map<Locale, Record<string, Document>>()

export async function fetchTranslationsRich(locale: Locale): Promise<Record<string, Document> | undefined> {
  let translationsRichText = translationsRichTextCache.get(locale)
  if (translationsRichText) return translationsRichText

  log('Downloading translations (rich text) for "%s"', locale)
  const entries = await getTranslationsRichText(locale)

  translationsRichText = entries.reduce((q, { fields }) => {
    return Object.assign(q, { [fields.title]: fields.content || {} })
  }, {} as Record<string, Document>)

  translationsRichTextCache.set(locale, translationsRichText)
  return translationsRichText
}

/* ========================================================================== */

export function fetchProduct(locale: Locale, slugOrEan: string): Promise<Dish | undefined> {
  return getDish(locale, slugOrEan).then((dish) => convert(dish))
}

/* ========================================================================== */

export function fetchAllProducts(locale: Locale): Promise<Dish[]> {
  return getDishes(locale).then((d) => d.map((d) => convert(d)).filter((d) => !! d))
}

/* ========================================================================== */

export function fetchProducts(locale: Locale, title: string): Promise<Dish[]> {
  return getDishList(locale, title).then((list) => convert(list)?.list || [])
}

/* ========================================================================== */

const globalRatingsCache = new Map<'stats', Map<string, Promise<Statistics | undefined>>>()

export async function fetchGlobalRatingStats(): Promise<Statistics | undefined> {
  let cache = globalRatingsCache.get('stats')
  if (!cache) globalRatingsCache.set('stats', cache = new Map())
  let stats = cache.get('stats')
  if (stats) return stats

  cache.set('stats', stats = (async () => {
    const response = await client.comments.stats()
    log('%cCaching global user rating statistics', 'color: gray')
    return response
  })())
  return stats
}

/* ========================================================================== */

const JOBS_URL = 'https://oetkerdigital1.recruitee.com/api/offers?tag=Juit'
const jobsCache = new Map<Locale, Map<string, Promise<any[]>>>()

export async function fetchJobs(locale: Locale): Promise<any[]> {
  let cache = jobsCache.get(locale)
  if (!cache) jobsCache.set(locale, cache = new Map())
  let jobs = cache.get(locale)
  if (jobs) return jobs

  cache.set(locale, jobs = (async () => {
    const response = await fetch(JOBS_URL) as any
    log('%cCaching jobs', 'color: gray')
    return convertJobs(await response.json())
  })())
  return jobs
}

/* ========================================================================== */

export async function fetchAlternateSlug(locale: Locale, slug: string, type: 'page' | 'dish', isRating: boolean): Promise<string> {
  const other_locale = locale === 'en' ? 'de' : 'en'
  let other_slug = slug

  const entry = type === 'page' ?
    await getPage(locale, slug) :
    await getDish(locale, slug)

  if (entry) {
    const other_entry = await client.content.getEntry(entry.sys.id, other_locale)
    other_slug = (other_entry?.fields.slug as string) || other_slug
    warn('Alternate slug resolved as', other_slug, { locale, slug, type, isRating })
  }

  const dishURL = type === 'dish' ? isRating ? '/rating' : other_locale === 'en' ? '/dishes' : '/gerichte' : ''
  return `/${other_locale}${dishURL}/${other_slug}`
}
